import { useState, useRef, useCallback, useEffect } from 'react';

import { useMounted, noop } from '@adsk/alloy-react-helpers';

export type UseVisibilityProps = {
  /**
   * whether the popover is showed or not. should be sent only for
   * controlled elements
   */
  show?: boolean;
  /* callback to be called on hide */
  onHide?: () => void;
  /* callback to be called on show */
  onShow?: () => void;
  /* delay before show in milliseconds */
  delayShow?: number;
  /* delay before hide in milliseconds */
  delayHide?: number;
};

function useVisibility({
  show: controlledShow,
  onHide: onHideProp = noop,
  onShow: onShowProp = noop,
  delayShow,
  delayHide,
}: UseVisibilityProps) {
  const isMountedRef = useMounted();
  const [isControlled, setIsControlled] = useState(
    controlledShow !== undefined
  );
  const [show, setShow] = useState(controlledShow || false);
  const timeoutShowRef = useRef<ReturnType<typeof setTimeout>>();
  const timeoutHideRef = useRef<ReturnType<typeof setTimeout>>();
  const handleShow = useCallback(
    () => (isControlled ? onShowProp() : setShow(true)),
    [isControlled, onShowProp]
  );
  const handleHide = useCallback(
    () => (isControlled ? onHideProp() : setShow(false)),
    [isControlled, onHideProp]
  );

  const controlledHandleToggle = useCallback(
    () => (show ? onHideProp() : onShowProp()),
    [show, onHideProp, onShowProp]
  );
  const handleToggle = useCallback(() => setShow(!show), [show]);

  useEffect(() => {
    setIsControlled(controlledShow !== undefined);
  }, [controlledShow]);

  const onShow = function () {
    clearTimeout(timeoutShowRef.current as number | undefined);
    clearTimeout(timeoutHideRef.current as number | undefined);

    timeoutShowRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        onShowProp();
        handleShow();
      }
    }, delayShow);
  };

  const onHide = function () {
    clearTimeout(timeoutShowRef.current as number | undefined);
    clearTimeout(timeoutHideRef.current as number | undefined);

    timeoutHideRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        onHideProp();
        handleHide();
      }
    }, delayHide);
  };

  return {
    show: isControlled ? !!controlledShow : show,
    onHide,
    onShow,
    handleShow,
    handleHide,
    handleToggle: isControlled ? controlledHandleToggle : handleToggle,
  };
}

export default useVisibility;
